(() => {
  const pad = (value) => String(value).padStart(2, "0");

  class EloRhodeProductPage {
    constructor(root) {
      this.root = root;
      this.gallery = root.querySelector("[data-elo24-gallery]");
      this.media = Array.from(root.querySelectorAll("[data-elo24-media]"));
      this.thumbs = Array.from(root.querySelectorAll("[data-elo24-thumb]"));
      this.progress = root.querySelector("[data-elo24-gallery-progress]");
      this.optionGroups = Array.from(root.querySelectorAll("[data-option-position]"));
      this.variantInput = root.querySelector("[data-elo24-variant-id]");
      this.add = root.querySelector("[data-elo24-add]");
      this.addLabel = root.querySelector("[data-elo24-add-label]");
      this.addPrice = root.querySelector("[data-elo24-add-price]");
      this.availableLabel = this.addLabel ? this.addLabel.textContent.trim() : "BUY";
      this.sticky = root.querySelector("[data-elo24-sticky]");
      this.stickyAdd = root.querySelector("[data-elo24-sticky-add]");
      this.stickyPrice = root.querySelector("[data-elo24-sticky-price]");
      this.lightbox = root.querySelector("[data-elo24-lightbox]");
      this.lightboxImage = root.querySelector("[data-elo24-lightbox-image]");
      this.activeIndex = 0;
      this.scrollFrame = 0;
      this.variants = this.readVariants();
      this.variantMeta = this.readVariantMeta();

      this.bindGallery();
      this.bindOptions();
      this.bindAccordions();
      this.bindSticky();
      this.bindLightbox();
      this.bindRoutineAdd();
      this.bindRitual();
      this.bindIngredientsLink();
      this.updateGalleryUI(0);
      this.updateOptionAvailability();
    }

    isMobile() {
      return window.matchMedia("(max-width: 820px)").matches;
    }

    readVariants() {
      try {
        return JSON.parse(this.root.querySelector("[data-elo24-variants]")?.textContent || "[]");
      } catch (_error) {
        return [];
      }
    }

    readVariantMeta() {
      return new Map(
        Array.from(this.root.querySelectorAll("[data-elo24-variant-meta]")).map((node) => [
          String(node.dataset.id),
          { ...node.dataset },
        ]),
      );
    }

    getGalleryIndex() {
      if (!this.gallery || !this.media.length) return 0;
      const left = this.gallery.getBoundingClientRect().left;
      return this.media.reduce((best, item, index) => {
        const current = Math.abs(item.getBoundingClientRect().left - left);
        const previous = Math.abs(this.media[best].getBoundingClientRect().left - left);
        return current < previous ? index : best;
      }, 0);
    }

    showMedia(index, behavior = "smooth") {
      if (!this.media.length) return;
      const safeIndex = Math.max(0, Math.min(index, this.media.length - 1));
      this.activeIndex = safeIndex;
      if (this.isMobile()) {
        this.gallery?.scrollTo({ left: this.media[safeIndex].offsetLeft, behavior });
      } else {
        this.media.forEach((item, itemIndex) => item.classList.toggle("is-active", itemIndex === safeIndex));
      }
      this.updateGalleryUI(safeIndex);
    }

    updateGalleryUI(index) {
      this.activeIndex = index;
      this.thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle("is-active", active);
        thumb.setAttribute("aria-current", String(active));
      });
      if (this.progress) {
        const width = ((index + 1) / Math.max(this.media.length, 1)) * 100;
        this.progress.parentElement?.style.setProperty("--elo24-gallery-progress", String(width) + "%");
      }
    }

    bindGallery() {
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener("click", () => this.showMedia(Number(thumb.dataset.elo24Thumb || 0)));
      });
      this.gallery?.addEventListener(
        "scroll",
        () => {
          window.cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = window.requestAnimationFrame(() => {
            if (this.isMobile()) this.updateGalleryUI(this.getGalleryIndex());
          });
        },
        { passive: true },
      );
      window.addEventListener(
        "resize",
        () => {
          if (!this.isMobile()) this.showMedia(this.activeIndex, "auto");
        },
        { passive: true },
      );
    }

    getSelections(replacement = null) {
      return this.optionGroups.map((group) => {
        if (replacement && Number(group.dataset.optionPosition) === replacement.position) return replacement.value;
        return group.querySelector(".is-selected")?.dataset.elo24OptionValue || "";
      });
    }

    findVariant(options) {
      return this.variants.find(
        (variant) =>
          variant.options?.length === options.length &&
          variant.options.every((value, index) => value === options[index]),
      );
    }

    bindOptions() {
      this.optionGroups.forEach((group) => {
        group.querySelectorAll("[data-elo24-option-value]").forEach((button) => {
          button.addEventListener("click", () => {
            if (button.disabled) return;
            group.querySelectorAll("[data-elo24-option-value]").forEach((item) => {
              const selected = item === button;
              item.classList.toggle("is-selected", selected);
              item.setAttribute("aria-pressed", String(selected));
            });
            const label = group.querySelector(
              "[data-elo24-option-name='" + group.dataset.optionPosition + "']",
            );
            if (label) label.textContent = button.dataset.elo24OptionValue;
            this.setVariant(this.findVariant(this.getSelections()));
            this.updateOptionAvailability();
          });
        });
      });

      this.root.querySelectorAll("[data-elo24-sticky-option]").forEach((button) => {
        button.addEventListener("click", () => {
          const mainButton = Array.from(
            this.optionGroups[0]?.querySelectorAll("[data-elo24-option-value]") || [],
          ).find((item) => item.dataset.elo24OptionValue === button.dataset.elo24StickyOption);
          mainButton?.click();
        });
      });
    }

    updateOptionAvailability() {
      this.optionGroups.forEach((group) => {
        const position = Number(group.dataset.optionPosition);
        group.querySelectorAll("[data-elo24-option-value]").forEach((button) => {
          const candidate = this.findVariant(
            this.getSelections({ position, value: button.dataset.elo24OptionValue }),
          );
          button.disabled = !candidate || !candidate.available;
          button.setAttribute("aria-disabled", String(button.disabled));
        });
      });
    }

    setVariant(variant) {
      if (!variant) {
        if (this.add) this.add.disabled = true;
        if (this.addLabel) this.addLabel.textContent = "НЕ Е НАЛИЧЕН";
        if (this.stickyAdd) this.stickyAdd.disabled = true;
        return;
      }

      const meta = this.variantMeta.get(String(variant.id)) || {};
      if (this.variantInput) this.variantInput.value = variant.id;
      if (this.addPrice) this.addPrice.textContent = "– " + (meta.price || "");
      if (this.stickyPrice) this.stickyPrice.textContent = "– " + (meta.price || "");
      const available = meta.available === "true" || variant.available === true;
      if (this.add) this.add.disabled = !available;
      if (this.stickyAdd) this.stickyAdd.disabled = !available;
      if (this.addLabel) this.addLabel.textContent = available ? this.availableLabel : "ИЗЧЕРПАН";

      const firstValue = variant.options?.[0];
      this.root.querySelectorAll("[data-elo24-sticky-option]").forEach((button) => {
        button.classList.toggle("is-selected", button.dataset.elo24StickyOption === firstValue);
      });

      if (meta.mediaId) {
        const index = this.media.findIndex((item) => item.dataset.mediaId === meta.mediaId);
        if (index >= 0) this.showMedia(index);
      }

      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    }

    bindAccordions() {
      const groups = this.root.querySelectorAll("[data-elo24-accordions]");
      groups.forEach((group) => {
        const items = Array.from(group.querySelectorAll("[data-elo24-accordion]"));
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const finish = (item, panel, open) => {
          item.classList.remove("is-animating");
          item.open = open;
          panel.style.height = open ? "auto" : "0px";
          item.querySelector("summary")?.setAttribute("aria-expanded", String(open));
        };

        const animate = (item, open) => {
          const panel = item.querySelector("[data-elo24-accordion-content]");
          if (!panel || item.classList.contains("is-animating")) return;
          if (reduced) return finish(item, panel, open);
          item.classList.add("is-animating");
          item.open = true;
          panel.style.height = open ? "0px" : String(panel.scrollHeight) + "px";
          panel.getBoundingClientRect();
          panel.style.height = open ? String(panel.scrollHeight) + "px" : "0px";
          const onEnd = (event) => {
            if (event.target !== panel || event.propertyName !== "height") return;
            panel.removeEventListener("transitionend", onEnd);
            finish(item, panel, open);
          };
          panel.addEventListener("transitionend", onEnd);
          window.setTimeout(() => {
            if (item.classList.contains("is-animating")) finish(item, panel, open);
          }, 760);
        };

        items.forEach((item) => {
          const panel = item.querySelector("[data-elo24-accordion-content]");
          const summary = item.querySelector("summary");
          if (!panel || !summary) return;
          panel.style.height = "0px";
          summary.setAttribute("aria-expanded", "false");
          summary.addEventListener("click", (event) => {
            event.preventDefault();
            const open = !item.open;
            if (open) items.forEach((other) => other !== item && other.open && animate(other, false));
            animate(item, open);
          });
        });
      });
    }

    bindSticky() {
      if (!this.add || !this.sticky) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.sticky.classList.toggle("is-visible", visible);
          this.sticky.setAttribute("aria-hidden", String(!visible));
        },
        { threshold: 0 },
      );
      observer.observe(this.add);
      this.stickyAdd?.addEventListener("click", () => {
        if (!this.add.disabled) this.add.click();
      });
    }

    bindLightbox() {
      if (!this.lightbox || !this.lightboxImage) return;
      this.root.querySelectorAll("[data-elo24-zoom]").forEach((button) => {
        button.addEventListener("click", () => {
          const image = button.querySelector("img");
          if (!image) return;
          this.lightboxImage.src = image.currentSrc || image.src;
          this.lightboxImage.alt = image.alt;
          this.lightbox.showModal?.();
        });
      });
      this.root.querySelector("[data-elo24-lightbox-close]")?.addEventListener("click", () => this.lightbox.close());
      this.lightbox.addEventListener("click", (event) => {
        if (event.target === this.lightbox) this.lightbox.close();
      });
    }

    bindRoutineAdd() {
      const checkboxes = Array.from(this.root.querySelectorAll("[data-elo24-routine-item]"));
      const cards = Array.from(this.root.querySelectorAll("[data-elo24-routine-card]"));
      const button = this.root.querySelector("[data-elo24-routine-add]");
      const message = this.root.querySelector("[data-elo24-routine-message]");
      if (!checkboxes.length || !button) return;

      const update = () => {
        const count = checkboxes.filter((input) => input.checked).length;
        cards.forEach((card) => {
          card.classList.toggle("is-selected", Boolean(card.querySelector("[data-elo24-routine-item]")?.checked));
        });
        button.disabled = count === 0;
        button.textContent = count ? "ДОБАВИ ИЗБРАНИЯ" : "ИЗБЕРИ ПРОДУКТ";
      };
      checkboxes.forEach((input) => input.addEventListener("change", update));

      cards.forEach((card) => {
        const input = card.querySelector("[data-elo24-routine-item]");
        const image = card.querySelector("[data-elo24-routine-image]");
        const price = card.querySelector("[data-elo24-routine-price]");
        const shadeName = card.querySelector("[data-elo24-routine-shade-name]");
        const shades = Array.from(card.querySelectorAll("[data-elo24-routine-variant]"));

        shades.forEach((shade) => {
          shade.addEventListener("click", () => {
            shades.forEach((item) => {
              const selected = item === shade;
              item.classList.toggle("is-selected", selected);
              item.setAttribute("aria-pressed", String(selected));
            });
            if (input) {
              input.value = shade.dataset.elo24RoutineVariant || input.value;
              input.checked = true;
            }
            if (price && shade.dataset.elo24RoutineVariantPrice) {
              price.textContent = shade.dataset.elo24RoutineVariantPrice;
            }
            if (shadeName && shade.dataset.elo24RoutineVariantName) {
              shadeName.textContent = shade.dataset.elo24RoutineVariantName;
            }
            if (image && shade.dataset.elo24RoutineVariantImage) {
              image.src = shade.dataset.elo24RoutineVariantImage;
              image.removeAttribute("srcset");
            }
            if (message) message.textContent = "";
            update();
          });
        });
      });

      button.addEventListener("click", async () => {
        const selected = checkboxes.filter((input) => input.checked);
        if (!selected.length || button.disabled) return;
        const cart = document.querySelector("cart-drawer");
        button.disabled = true;
        button.textContent = "ДОБАВЯНЕ...";
        if (message) message.textContent = "";
        try {
          const body = { items: selected.map((input) => ({ id: Number(input.value), quantity: 1 })) };
          if (cart?.getSectionsToRender) {
            body.sections = cart.getSectionsToRender().map((section) => section.id).join(",");
            body.sections_url = window.location.pathname;
            cart.setActiveElement?.(button);
          }
          const configuredRoute = window.routes?.cart_add_url || "/cart/add";
          const endpoint = configuredRoute.endsWith(".js")
            ? configuredRoute
            : configuredRoute.replace(/\/$/, "") + ".js";
          const response = await fetch(endpoint, {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(body),
          });
          const responseText = await response.text();
          let data;
          try {
            data = JSON.parse(responseText);
          } catch (_error) {
            throw new Error("Количката не върна валиден отговор. Моля, опитай отново.");
          }
          if (!response.ok || data.status) throw new Error(data.description || data.message || "Неуспешно добавяне");
          cart?.renderContents?.(data);
          selected.forEach((input) => {
            input.checked = false;
          });
          if (message) message.textContent = "Добавено в количката.";
        } catch (error) {
          if (message) message.textContent = error.message || "Възникна грешка. Опитай отново.";
        } finally {
          update();
        }
      });
    }

    bindRitual() {
      const ritual = this.root.querySelector("[data-elo24-ritual]");
      if (!ritual) return;

      const steps = Array.from(ritual.querySelectorAll("[data-elo24-ritual-step]"));
      const visuals = Array.from(ritual.querySelectorAll("[data-elo24-ritual-visual]"));
      const panels = Array.from(ritual.querySelectorAll("[data-elo24-ritual-panel]"));

      const hasContent = (index) =>
        visuals.some((item) => item.dataset.elo24RitualVisual === index) &&
        panels.some((item) => item.dataset.elo24RitualPanel === index);

      const activate = (index) => {
        if (!hasContent(index)) return;
        steps.forEach((item) => {
          const isActive = item.dataset.elo24RitualStep === index;
          item.classList.toggle("is-active", isActive);
          item.setAttribute("aria-selected", String(isActive));
        });
        visuals.forEach((item) => {
          const isActive = item.dataset.elo24RitualVisual === index;
          item.classList.toggle("is-active", isActive);
          item.setAttribute("aria-hidden", String(!isActive));
        });
        panels.forEach((item) => {
          const isActive = item.dataset.elo24RitualPanel === index;
          item.classList.toggle("is-active", isActive);
          item.setAttribute("aria-hidden", String(!isActive));
        });
      };

      steps.forEach((step) => {
        const index = step.dataset.elo24RitualStep;
        const available = hasContent(index);
        step.hidden = !available;
        step.disabled = !available;
        if (available) step.addEventListener("click", () => activate(index));
      });

      const initial = steps.find((step) => !step.hidden && step.classList.contains("is-active")) || steps.find((step) => !step.hidden);
      if (initial) activate(initial.dataset.elo24RitualStep);
    }

    bindIngredientsLink() {
      this.root.querySelector("[data-elo24-open-ingredients]")?.addEventListener("click", () => {
        const item = this.root.querySelectorAll("[data-elo24-accordion]")[2];
        const panel = item?.querySelector("[data-elo24-accordion-content]");
        if (!item || !panel) return;
        item.open = true;
        panel.style.height = "auto";
        item.querySelector("summary")?.setAttribute("aria-expanded", "true");
        item.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }

  const initializeRelated = (section) => {
    if (!section || section.dataset.elo24Ready === "true") return;
    const rail = section.querySelector("[data-elo24-related-rail]");
    const cards = Array.from(section.querySelectorAll("[data-elo24-related-card]"));
    const dots = Array.from(section.querySelectorAll("[data-elo24-related-dot]"));
    if (!rail || cards.length < 2 || cards.length !== dots.length) return;
    section.dataset.elo24Ready = "true";
    let frame = 0;
    const update = () => {
      frame = 0;
      const left = rail.getBoundingClientRect().left;
      let active = 0;
      let distance = Infinity;
      cards.forEach((card, index) => {
        const next = Math.abs(card.getBoundingClientRect().left - left);
        if (next < distance) {
          distance = next;
          active = index;
        }
      });
      dots.forEach((dot, index) => {
        dot.classList.toggle("is-active", index === active);
        dot.setAttribute("aria-current", index === active ? "true" : "false");
      });
    };
    rail.addEventListener(
      "scroll",
      () => {
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      },
      { passive: true },
    );
    dots.forEach((dot, index) => {
      dot.addEventListener("click", () => rail.scrollTo({ left: cards[index].offsetLeft - rail.offsetLeft, behavior: "smooth" }));
    });
    update();
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll("[data-elo24-product]").forEach((root) => {
      if (root.dataset.elo24Ready === "true") return;
      root.dataset.elo24Ready = "true";
      new EloRhodeProductPage(root);
    });
    scope.querySelectorAll("[data-elo24-related]").forEach(initializeRelated);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
})();
